import { lazy, Suspense } from "react";
import { AnalyticsDashboard } from "./AnalyticsDashboard";
import { OrganPage } from "./OrganPage";
import { GamificationHero } from "./GamificationHero";

// Lazy load non-critical pages
const QuizPage = lazy(() => import("./QuizPage").then(m => ({ default: m.QuizPage })));
const LearningProgressPage = lazy(() => import("./LearningProgressPage").then(m => ({ default: m.LearningProgressPage })));
const LeaderboardPage = lazy(() => import("./LeaderboardPage").then(m => ({ default: m.LeaderboardPage })));
const LibraryPage = lazy(() => import("./LibraryPage").then(m => ({ default: m.LibraryPage })));
const AdminPanel = lazy(() => import("./AdminPanel").then(m => ({ default: m.AdminPanel })));
const ProfilePage = lazy(() => import("./ProfilePage").then(m => ({ default: m.ProfilePage })));
const DiscussionPage = lazy(() => import("./DiscussionPage").then(m => ({ default: m.DiscussionPage })));
const NotificationsPage = lazy(() => import("./NotificationsPage").then(m => ({ default: m.NotificationsPage })));
const FAQPage = lazy(() => import("./FAQPage").then(m => ({ default: m.FAQPage })));
const BookmarksPage = lazy(() => import("./BookmarksPage").then(m => ({ default: m.BookmarksPage })));
const AdminCRM = lazy(() => import("./AdminCRM").then(m => ({ default: m.AdminCRM })));
const TestResultsPage = lazy(() => import("./TestResultsPage").then(m => ({ default: m.TestResultsPage })));
const FounderDashboard = lazy(() => import("./FounderDashboard").then(m => ({ default: m.FounderDashboard })));
const EventsPage = lazy(() => import("./EventsPage").then(m => ({ default: m.EventsPage })));
const LockedOrganView = lazy(() => import("./LockedOrganView").then(m => ({ default: m.LockedOrganView })));

const PageLoader = () => (
  <div className="space-y-4 animate-pulse" data-testid="page-loader">
    {/* Hero card skeleton */}
    <div className="rounded-[20px] bg-white border border-[rgba(0,0,0,0.06)] p-6">
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-2xl bg-[#F2F2F2]" />
        <div className="flex-1 space-y-2">
          <div className="h-5 bg-[#F2F2F2] rounded-lg w-2/3" />
          <div className="h-3 bg-[#F7F7F7] rounded-md w-1/3" />
        </div>
      </div>
    </div>
    {/* Stat grid skeleton */}
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {[0, 1, 2, 3].map(i => (
        <div key={i} className="rounded-[20px] bg-white border border-[rgba(0,0,0,0.06)] p-4">
          <div className="w-9 h-9 rounded-xl bg-[#F2F2F2] mb-2" />
          <div className="h-5 bg-[#F2F2F2] rounded-md w-12 mb-1.5" />
          <div className="h-3 bg-[#F7F7F7] rounded-md w-16" />
        </div>
      ))}
    </div>
    {/* Content card skeleton */}
    <div className="rounded-[20px] bg-white border border-[rgba(0,0,0,0.06)] p-4 space-y-3">
      <div className="h-4 bg-[#F2F2F2] rounded-md w-1/4" />
      {[0, 1, 2].map(i => (
        <div key={i} className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#F7F7F7]" />
          <div className="flex-1 space-y-2">
            <div className="h-3 bg-[#F2F2F2] rounded-md" />
            <div className="h-2.5 bg-[#F7F7F7] rounded-md w-2/3" />
          </div>
        </div>
      ))}
    </div>
    {/* Subtle loading badge */}
    <div className="fixed bottom-6 right-6 flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#0A0A0A] text-white text-xs font-semibold shadow-lg z-50">
      <div className="w-3 h-3 border-2 border-[#2563EB] border-t-transparent rounded-full animate-spin" />
      <span>Memuat...</span>
    </div>
  </div>
);

export const DashboardContent = ({
  syllabus,
  activeSection,
  onDeleteFile,
  onAddNode,
  onDeleteNode,
  onRefresh,
  apiUrl,
  totalOrgans,
  totalFiles,
  totalTopics,
  readPoints,
  onToggleRead,
  personalNotes,
  onSaveNote,
  onSectionChange,
  readProgress,
  studentName,
  studentNiche,
  studentBatch,
  lang,
  onUpdateProfile,
  nicheList,
  bookmarkedIds,
  onToggleBookmark,
  userRole,
  authToken,
  organAccess
}) => {
  const organSections = syllabus.filter(n => n.title !== 'SLIDE');

  const activeOrgan = organSections.find(o => o.id === activeSection);
  const activeOrganIndex = organSections.findIndex(o => o.id === activeSection);
  const isOrganUnlocked = (idx) => organAccess?.all_unlocked || (organAccess?.unlocked || []).includes(idx + 1);

  return (
    <Suspense fallback={<PageLoader />}>
    <div>
      {/* Dashboard with analytics charts */}
      {activeSection === 'dashboard' && (
        <>
          <GamificationHero apiUrl={apiUrl} studentName={studentName} lang={lang} />
          <AnalyticsDashboard
            apiUrl={apiUrl}
            readProgress={readProgress}
            organSections={organSections}
            onSectionChange={onSectionChange}
            lang={lang}
            studentName={studentName}
            organ1Unlocked={isOrganUnlocked(0)}
          />
        </>
      )}

      {/* Individual Organ page */}
      {activeOrgan && (
        isOrganUnlocked(activeOrganIndex) ? (
          <OrganPage
            organ={activeOrgan}
            organIndex={activeOrganIndex}
            onDeleteFile={onDeleteFile}
            onAddNode={onAddNode}
            onDeleteNode={onDeleteNode}
            onRefresh={onRefresh}
            apiUrl={apiUrl}
            readPoints={readPoints}
            onToggleRead={onToggleRead}
            personalNotes={personalNotes}
            onSaveNote={onSaveNote}
            lang={lang}
            bookmarkedIds={bookmarkedIds}
            onToggleBookmark={onToggleBookmark}
            isAdmin={userRole === 'admin'}
            studentName={studentName}
          />
        ) : (
          <LockedOrganView organIndex={activeOrganIndex + 1} organTitle={activeOrgan.title} onSectionChange={onSectionChange} lang={lang} />
        )
      )}

      {/* Quiz page */}
      {activeSection === 'quiz' && (
        <QuizPage apiUrl={apiUrl} organSections={organSections} studentName={studentName} readPoints={readPoints} lang={lang} studentNiche={studentNiche} studentBatch={studentBatch} />
      )}

      {/* Leaderboard page */}
      {activeSection === 'leaderboard' && (
        <LeaderboardPage apiUrl={apiUrl} currentStudent={studentName} lang={lang} />
      )}

      {/* BSS Library page */}
      {activeSection === 'library' && (
        <LibraryPage apiUrl={apiUrl} lang={lang} isAdmin={userRole === 'admin'} />
      )}

      {/* FAQ page */}
      {activeSection === 'faq' && (
        <FAQPage apiUrl={apiUrl} lang={lang} />
      )}

      {/* Bookmarks page */}
      {activeSection === 'bookmarks' && (
        <BookmarksPage apiUrl={apiUrl} studentName={studentName} lang={lang} />
      )}

      {/* Admin Panel - admin only */}
      {activeSection === 'admin' && userRole === 'admin' && (
        <AdminPanel apiUrl={apiUrl} lang={lang} />
      )}

      {/* Profile Page */}
      {activeSection === 'profile' && (
        <ProfilePage apiUrl={apiUrl} studentName={studentName} studentNiche={studentNiche} studentBatch={studentBatch} onUpdateProfile={onUpdateProfile} lang={lang} nicheList={nicheList} />
      )}

      {/* Community Discussion */}
      {activeSection === 'community' && (
        <DiscussionPage apiUrl={apiUrl} studentName={studentName} lang={lang} />
      )}

      {/* Notifications full page */}
      {activeSection === 'notifications' && (
        <NotificationsPage apiUrl={apiUrl} lang={lang} onNavigate={onSectionChange} />
      )}

      {/* Learning Progress page */}
      {activeSection === 'progress' && (
        <LearningProgressPage
          apiUrl={apiUrl}
          totalOrgans={totalOrgans}
          totalTopics={totalTopics}
          totalFiles={totalFiles}
          readProgress={readProgress}
          organSections={organSections}
          onSectionChange={onSectionChange}
          lang={lang}
        />
      )}

      {/* TEST - admin-uploaded test papers per Organ */}
      {activeSection === 'test' && (
        <TestResultsPage
          apiUrl={apiUrl}
          authToken={authToken}
          isAdmin={userRole === 'admin'}
          lang={lang}
        />
      )}

      {/* Founder Dashboard */}
      {activeSection === 'founder' && (
        <FounderDashboard apiUrl={apiUrl} authToken={authToken} />
      )}

      {/* Events page (user self-registration) */}
      {activeSection === 'events' && (
        <EventsPage apiUrl={apiUrl} authToken={authToken} lang={lang} />
      )}
    </div>
    </Suspense>
  );
};
